import { noop } from 'lodash';
import { ArdoqId, ViewIds } from '@ardoq/api-types';
import { componentGraphSelectionChanged } from '@ardoq/component-selection';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  updateViewContext as updateViewContextAction,
  updateViewSettings as updateViewSettingsAction,
  type ViewContext,
} from '@ardoq/view-settings';
import {
  hideDetailsDrawer as hideDetailsDrawerAction,
  openDetailsDrawer,
  updateDetailsDrawer as updateDetailsDrawerAction,
} from 'appLayout/ardoqStudio/detailsDrawer/actions';
import {
  ContextMenuRequest,
  contextMenuRequested,
} from 'contextMenus/contextMenuRequested';
import { componentSelectedInPresentationMode } from 'streams/components/ComponentActions';
import { recordRenderComplete } from 'tracking/durationTracking/viewPerformance/viewPerformanceTracking';
import { trackEvent } from 'tracking/tracking';
import { componentSelectedInQuickExploreMode } from 'traverseOneLevel/actions';
import type { BlocksArrangement } from 'tabview/assistantBlocks/arrangementRecipe';
import type {
  BlocksViewCommands,
  BlocksViewSettings,
  LayoutData,
  ViewActionClickEvent,
  ViewpointModeBlocksDataLayerCommands,
} from './types';

const VIEW_ID = ViewIds.BLOCKS;

const updateViewContext = (viewContext: ViewContext) =>
  dispatchAction(updateViewContextAction(viewContext));

const updateViewSettings = (settings: Partial<BlocksViewSettings>) =>
  dispatchAction(
    updateViewSettingsAction({ viewId: VIEW_ID, settings, persistent: true })
  );

const updateViewLegendSettings = (isLegendActive: boolean) =>
  updateViewSettings({ isLegendActive });

const saveLayoutData = (layoutData: LayoutData) =>
  updateViewSettings({ layoutData });

const saveResetLayout = (
  layoutData: LayoutData,
  arrangements: BlocksArrangement[]
) => updateViewSettings({ layoutData, arrangements });

const saveArrangedLayout = (layoutData: LayoutData) =>
  updateViewSettings({ layoutData, pendingArrangementContainerIds: [] });

const updateGraphSelection = (componentIds: ArdoqId[]) =>
  dispatchAction(
    componentGraphSelectionChanged({ graphSelection: componentIds })
  );

const updateDetailsDrawer = (componentId: ArdoqId) =>
  dispatchAction(updateDetailsDrawerAction([componentId]));

const hideDetailsDrawer = () => dispatchAction(hideDetailsDrawerAction());

const openContextMenu = (request: ContextMenuRequest) =>
  dispatchAction(contextMenuRequested(request));

const trackViewAction = (viewAction: ViewActionClickEvent) =>
  trackEvent('View action', { viewId: VIEW_ID, ...viewAction });

const selectComponentInPresentationMode = (componentId: ArdoqId) =>
  dispatchAction(componentSelectedInPresentationMode({ id: componentId }));

const selectComponentInQuickExploreMode = (componentId: ArdoqId) =>
  dispatchAction(componentSelectedInQuickExploreMode(componentId));

const openSelectedComponentsInDetailsDrawer = (
  _componentId: ArdoqId,
  selectedComponentIds: ArdoqId[]
) => dispatchAction(openDetailsDrawer(selectedComponentIds));

export type ViewpointModeBlocksViewCommandsConfig = {
  isPresentationMode: boolean;
  isQuickExploreSelectionEnabled: boolean;
};

export const getComponentDoubleClickHandler = ({
  isPresentationMode,
  isQuickExploreSelectionEnabled,
}: ViewpointModeBlocksViewCommandsConfig): BlocksViewCommands['onComponentDoubleClick'] => {
  if (isPresentationMode) {
    return selectComponentInPresentationMode;
  }
  if (isQuickExploreSelectionEnabled) {
    return selectComponentInQuickExploreMode;
  }
  return openSelectedComponentsInDetailsDrawer;
};

/**
 * Design decision: opening URLs (canvas URL icon, tooltip links, context menu
 * "Open URL") is deliberately not a command. A URL opens the only way the app
 * knows how, so making it configurable would only add code complexity —
 * threading a callback through tooltips and context-menu builders — with no
 * host that needs a different behaviour.
 */
export const createViewpointModeBlocksViewCommands = (
  config: ViewpointModeBlocksViewCommandsConfig
): BlocksViewCommands => ({
  updateViewSettings,
  updateViewContext,
  onLegendToggle: updateViewLegendSettings,
  recordRenderComplete,
  onLayoutChange: saveLayoutData,
  onLayoutReset: saveResetLayout,
  saveArrangedLayout,
  onSelectionChange: updateGraphSelection,
  updateDetailsDrawer,
  hideDetailsDrawer,
  onComponentClick: noop,
  onComponentDoubleClick: getComponentDoubleClickHandler(config),
  openContextMenu,
  onViewActionClick: trackViewAction,
});

export const NOOP_VIEWPOINT_MODE_BLOCKS_DATA_LAYER_COMMANDS: ViewpointModeBlocksDataLayerCommands =
  {
    updateViewSettings: noop,
    updateViewContext: noop,
    onLegendToggle: noop,
    recordRenderComplete: noop,
    onLayoutChange: noop,
    onLayoutReset: noop,
    saveArrangedLayout: noop,
    onSelectionChange: noop,
    updateDetailsDrawer: noop,
    hideDetailsDrawer: noop,
    onComponentClick: noop,
    onComponentDoubleClick: noop,
    openContextMenu: noop,
    onViewActionClick: noop,
    recordViewModelReady: noop,
    updateViewProperties: noop,
    updateViewEmptyStateData: noop,
    updateViewComponentIds: noop,
  };
